import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { LoadingCard } from "../components/LoadingSpinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { HardHat, Calendar, Users, StickyNote, Check } from "lucide-react";
import { productionService } from "../services/productionService";
import { productionStageService } from "../services/productionStageService";
import { crewService } from "../services/crewService";
import { useToast } from "../hooks/use-toast";

const STATUS_OPTIONS = [
  { value: "not_started", label: "Not Started" },
  { value: "in_progress", label: "In Progress" },
  { value: "complete", label: "Complete" },
  { value: "skipped", label: "Skipped" },
  { value: "blocked", label: "Blocked" },
];

const STATUS_BADGE_CLASSES = {
  not_started: "bg-gray-200 text-gray-800",
  in_progress: "bg-blue-500 text-white",
  complete: "bg-green-500 text-white",
  skipped: "bg-slate-400 text-white",
  blocked: "bg-red-500 text-white",
};

export default function Production() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [draggingProjectId, setDraggingProjectId] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [editingStageValue, setEditingStageValue] = useState(null);
  const [stageEditValues, setStageEditValues] = useState({});

  const { data: stagesData, isLoading: stagesLoading } = useQuery({
    queryKey: ["production-stages"],
    queryFn: () => productionStageService.getAll(),
  });

  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ["production-projects"],
    queryFn: () => productionService.listProjects(),
  });

  const { data: crewsData } = useQuery({
    queryKey: ["crews", "active"],
    queryFn: () => crewService.getAll(true),
  });

  const moveStageMutation = useMutation({
    mutationFn: ({ projectId, stageValue }) =>
      productionService.moveToStage(projectId, stageValue),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["production-projects"] });
      toast({ description: "Project moved" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: error?.detail || "Failed to move project" });
    },
  });

  const updateStageMutation = useMutation({
    mutationFn: ({ projectId, stageValue, payload }) =>
      productionService.updateStage(projectId, stageValue, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["production-projects"] });
      toast({ description: "Stage updated" });
      setEditingProject(null);
      setEditingStageValue(null);
      setStageEditValues({});
    },
    onError: (error) => {
      toast({ variant: "destructive", description: error?.detail || "Failed to update stage" });
    },
  });

  const stopProductionMutation = useMutation({
    mutationFn: (projectId) => productionService.stopProduction(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["production-projects"] });
      toast({ description: "Project removed from production" });
    },
    onError: (error) => {
      toast({ variant: "destructive", description: error?.detail || "Failed to stop production" });
    },
  });

  const activeStages = useMemo(
    () => (stagesData?.data || []).filter((s) => s.is_active),
    [stagesData]
  );

  const standardStages = useMemo(
    () => activeStages.filter((s) => !s.is_optional),
    [activeStages]
  );

  const optionalStages = useMemo(
    () => activeStages.filter((s) => s.is_optional),
    [activeStages]
  );

  const projectsByStage = useMemo(() => {
    const grouped = {};
    activeStages.forEach((s) => {
      grouped[s.value] = [];
    });
    (projectsData?.data || []).forEach((p) => {
      const stage = p.current_production_stage;
      if (stage && grouped[stage] !== undefined) {
        grouped[stage].push(p);
      } else if (activeStages.length > 0) {
        // Put projects with no stage in the first stage as fallback
        grouped[activeStages[0].value].push(p);
      }
    });
    return grouped;
  }, [projectsData, activeStages]);

  const crewById = useMemo(() => {
    const map = {};
    (crewsData?.data || []).forEach((c) => {
      map[c.id] = c;
    });
    return map;
  }, [crewsData]);

  const handleDragStart = (e, projectId) => {
    setDraggingProjectId(projectId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, stageValue) => {
    e.preventDefault();
    if (draggingProjectId) {
      moveStageMutation.mutate({ projectId: draggingProjectId, stageValue });
      setDraggingProjectId(null);
    }
  };

  const openStageEditor = (project, stageValue) => {
    const progress = (project.production_stages || []).find(
      (s) => s.stage_value === stageValue
    ) || {};
    setEditingProject(project);
    setEditingStageValue(stageValue);
    setStageEditValues({
      status: progress.status || "not_started",
      start_date: progress.start_date || "",
      completion_date: progress.completion_date || "",
      crew_id: progress.crew_id || "",
      notes: progress.notes || "",
    });
  };

  if (stagesLoading || projectsLoading) {
    return <LoadingCard />;
  }

  if (activeStages.length === 0) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>Production</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">
              No active production stages configured. Go to{" "}
              <a href="/settings?tab=production-stages" className="text-blue-600 hover:underline">
                Settings → Production Stages
              </a>{" "}
              to set them up.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <HardHat className="mr-2 h-6 w-6 text-blue-600" />
            Production Workflow
          </h1>
          <p className="text-sm text-gray-500">
            Drag projects between columns to move them through stages. Click a stage to edit progress, crew, or notes.
          </p>
        </div>
        <div className="text-sm text-gray-500">
          {(projectsData?.data || []).length} projects in production
        </div>
      </div>

      {/* Kanban board — two rows */}
      {/* Standard stages row */}
      <div className="overflow-x-auto pb-2">
        <div className="flex gap-3" style={{ minWidth: "max-content" }}>
          {standardStages.map((stage) => {
            const projects = projectsByStage[stage.value] || [];
            return (
              <div
                key={stage.value}
                className="w-64 flex-shrink-0 bg-gray-50 rounded-lg border"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.value)}
              >
                <div
                  className="p-2.5 rounded-t-lg border-b"
                  style={{ backgroundColor: stage.color || "#64748b", color: "white" }}
                >
                  <div className="font-semibold text-sm">{stage.label}</div>
                  <div className="text-xs opacity-90">{projects.length} project{projects.length !== 1 ? "s" : ""}</div>
                </div>
                <div className="p-2 space-y-2 min-h-[150px]">
                  {projects.map((project) => {
                    const progress = (project.production_stages || []).find((s) => s.stage_value === stage.value);
                    const status = progress?.status || "not_started";
                    const crew = progress?.crew_id ? crewById[progress.crew_id] : null;
                    return (
                      <div
                        key={project.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, project.id)}
                        className="bg-white rounded border p-2 shadow-sm cursor-move hover:shadow-md transition-shadow"
                        onClick={() => openStageEditor(project, stage.value)}
                      >
                        <div className="font-medium text-sm truncate">{project.name}</div>
                        <div className="text-xs text-gray-500 truncate">{project.customer_name}</div>
                        <div className="mt-1 flex items-center justify-between gap-1">
                          <Badge className={`text-[10px] ${STATUS_BADGE_CLASSES[status]}`}>
                            {STATUS_OPTIONS.find((o) => o.value === status)?.label || status}
                          </Badge>
                          {crew && (
                            <div
                              className="text-[10px] px-1.5 py-0.5 rounded text-white truncate max-w-[90px]"
                              style={{ backgroundColor: crew.color || "#2563eb" }}
                              title={crew.name}
                            >
                              {crew.name}
                            </div>
                          )}
                        </div>
                        {progress?.notes && (
                          <div className="mt-1 text-[10px] text-gray-500 flex items-center gap-1 truncate">
                            <StickyNote className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{progress.notes}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {projects.length === 0 && (
                    <div className="text-xs text-gray-400 text-center py-4 italic">Drop projects here</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Optional stages row */}
      {optionalStages.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">Optional Stages</div>
          <div className="overflow-x-auto pb-2">
            <div className="flex gap-3" style={{ minWidth: "max-content" }}>
              {optionalStages.map((stage) => {
                const projects = projectsByStage[stage.value] || [];
                return (
                  <div
                    key={stage.value}
                    className="w-64 flex-shrink-0 bg-amber-50/50 rounded-lg border border-amber-200"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, stage.value)}
                  >
                    <div
                      className="p-2.5 rounded-t-lg border-b"
                      style={{ backgroundColor: stage.color || "#64748b", color: "white" }}
                    >
                      <div className="font-semibold text-sm">{stage.label}</div>
                      <div className="text-xs opacity-90">{projects.length} project{projects.length !== 1 ? "s" : ""}</div>
                    </div>
                    <div className="p-2 space-y-2 min-h-[120px]">
                      {projects.map((project) => {
                        const progress = (project.production_stages || []).find((s) => s.stage_value === stage.value);
                        const status = progress?.status || "not_started";
                        const crew = progress?.crew_id ? crewById[progress.crew_id] : null;
                        return (
                          <div
                            key={project.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, project.id)}
                            className="bg-white rounded border p-2 shadow-sm cursor-move hover:shadow-md transition-shadow"
                            onClick={() => openStageEditor(project, stage.value)}
                          >
                            <div className="font-medium text-sm truncate">{project.name}</div>
                            <div className="text-xs text-gray-500 truncate">{project.customer_name}</div>
                            <div className="mt-1 flex items-center justify-between gap-1">
                              <Badge className={`text-[10px] ${STATUS_BADGE_CLASSES[status]}`}>
                                {STATUS_OPTIONS.find((o) => o.value === status)?.label || status}
                              </Badge>
                              {crew && (
                                <div
                                  className="text-[10px] px-1.5 py-0.5 rounded text-white truncate max-w-[90px]"
                                  style={{ backgroundColor: crew.color || "#2563eb" }}
                                  title={crew.name}
                                >
                                  {crew.name}
                                </div>
                              )}
                            </div>
                            {progress?.notes && (
                              <div className="mt-1 text-[10px] text-gray-500 flex items-center gap-1 truncate">
                                <StickyNote className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">{progress.notes}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {projects.length === 0 && (
                        <div className="text-xs text-gray-400 text-center py-4 italic">Drop projects here</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Stage editor dialog */}
      <Dialog
        open={!!editingProject && !!editingStageValue}
        onOpenChange={(open) => {
          if (!open) {
            setEditingProject(null);
            setEditingStageValue(null);
            setStageEditValues({});
          }
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingProject?.name}</DialogTitle>
            <DialogDescription>
              Stage:{" "}
              <strong>
                {
                  activeStages.find((s) => s.value === editingStageValue)?.label
                }
              </strong>
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Status</Label>
              <Select
                value={stageEditValues.status || "not_started"}
                onValueChange={(value) =>
                  setStageEditValues((prev) => ({ ...prev, status: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={stageEditValues.start_date || ""}
                  onChange={(e) =>
                    setStageEditValues((prev) => ({ ...prev, start_date: e.target.value }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label>Completion Date</Label>
                <Input
                  type="date"
                  value={stageEditValues.completion_date || ""}
                  onChange={(e) =>
                    setStageEditValues((prev) => ({ ...prev, completion_date: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Assigned Crew</Label>
              <Select
                value={stageEditValues.crew_id || "__none__"}
                onValueChange={(value) =>
                  setStageEditValues((prev) => ({
                    ...prev,
                    crew_id: value === "__none__" ? "" : value,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="No crew assigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No crew assigned</SelectItem>
                  {(crewsData?.data || []).map((crew) => (
                    <SelectItem key={crew.id} value={crew.id}>
                      {crew.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Notes</Label>
              <Textarea
                value={stageEditValues.notes || ""}
                onChange={(e) =>
                  setStageEditValues((prev) => ({ ...prev, notes: e.target.value }))
                }
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0 sm:justify-between">
            <Button
              variant="outline"
              className="text-red-600"
              onClick={() => {
                if (window.confirm("Remove this project from the production workflow?")) {
                  stopProductionMutation.mutate(editingProject.id);
                  setEditingProject(null);
                  setEditingStageValue(null);
                  setStageEditValues({});
                }
              }}
            >
              Remove from Production
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setEditingProject(null);
                  setEditingStageValue(null);
                  setStageEditValues({});
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  updateStageMutation.mutate({
                    projectId: editingProject.id,
                    stageValue: editingStageValue,
                    payload: {
                      status: stageEditValues.status || null,
                      start_date: stageEditValues.start_date || null,
                      completion_date: stageEditValues.completion_date || null,
                      crew_id: stageEditValues.crew_id || null,
                      notes: stageEditValues.notes || null,
                    },
                  });
                }}
                disabled={updateStageMutation.isPending}
              >
                <Check className="mr-2 h-4 w-4" />
                Save
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
